import React, { useState } from 'react';
import { AlertTriangle, Calendar, MapPinned, Users } from 'lucide-react';
import RoutePlannerPriorityList from './RoutePlannerPriorityList';
import RoutePlannerRouteList from './RoutePlannerRouteList';

function RoutePlannerControlPanel({
  selectedDate,
  setSelectedDate,
  strategy,
  setStrategy,
  serviceMinutes,
  setServiceMinutes,
  maxPackagesDefault,
  setMaxPackagesDefault,
  useGoogle,
  setUseGoogle,
  preview,
  selectedCount,
  availableDrivers,
  onOpenDrivers,
  onLoadLatestPlan,
  plan,
  loading,
  publishPlan,
  moveStop,
  moveStopToRoute,
  moveStopsToDestination,
  onResequenceRoute,
  moveDrafts,
  setMoveDrafts,
  onTogglePriority,
  onMarkPostponedPriority,
  onAssignUnassigned,
}) {
  const [open, setOpen] = useState({ rules: true, routes: true, priorities: false, unassigned: true });

  function toggle(key) {
    setOpen((current) => ({ ...current, [key]: !current[key] }));
  }

  return (
    <aside className="flex min-h-0 flex-col rounded-lg border border-slate-200 bg-white xl:h-full">
      <div className="border-b border-slate-200 p-3">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPinned className="h-5 w-5 text-brand-green" />
            <h2 className="font-bold text-slate-900">Entregas</h2>
          </div>
          <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600">
            {preview?.summary?.total || 0}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <Metric label="Sin ubic." value={preview?.summary?.missing_location || 0} tone="warn" />
          <Metric label="Prioridad" value={preview?.summary?.priority || 0} tone="hot" />
          <Metric label="Drivers" value={selectedCount} tone="ok" />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        <PanelSection title="Fecha y reglas" icon={<Calendar className="h-4 w-4" />} open={open.rules} onToggle={() => toggle('rules')}>
          <RulesForm
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            strategy={strategy}
            setStrategy={setStrategy}
            serviceMinutes={serviceMinutes}
            setServiceMinutes={setServiceMinutes}
            maxPackagesDefault={maxPackagesDefault}
            setMaxPackagesDefault={setMaxPackagesDefault}
            useGoogle={useGoogle}
            setUseGoogle={setUseGoogle}
            onOpenDrivers={onOpenDrivers}
            onLoadLatestPlan={onLoadLatestPlan}
            latestPlan={preview?.latest_plan}
          />
        </PanelSection>

        <PanelSection
          title={plan ? `Rutas creadas #${plan.id}` : 'Rutas creadas'}
          meta={plan ? `${plan.summary?.assigned_stops || 0} asignadas` : 'Sin plan'}
          open={open.routes}
          onToggle={() => toggle('routes')}
          bodyClassName="max-h-[54vh] overflow-y-auto"
        >
          <RoutePlannerRouteList
            plan={plan}
            loading={loading}
            publishPlan={publishPlan}
            moveStop={moveStop}
            moveStopToRoute={moveStopToRoute}
            moveStopsToDestination={moveStopsToDestination}
            onResequenceRoute={onResequenceRoute}
            moveDrafts={moveDrafts}
            setMoveDrafts={setMoveDrafts}
            availableDrivers={availableDrivers}
          />
        </PanelSection>

        <PanelSection
          title="Prioridades del dia"
          meta={`${preview?.summary?.priority || 0} prioridad`}
          open={open.priorities}
          onToggle={() => toggle('priorities')}
          bodyClassName="max-h-[44vh] overflow-y-auto"
        >
          <RoutePlannerPriorityList
            deliveries={preview?.deliveries || []}
            selectedDate={selectedDate}
            onTogglePriority={onTogglePriority}
            onMarkPostponedPriority={onMarkPostponedPriority}
          />
        </PanelSection>

        <PanelSection
          title="Sin asignar"
          meta={`${plan?.unassigned?.length || 0}`}
          tone={plan?.unassigned?.length ? 'warn' : 'default'}
          open={open.unassigned}
          onToggle={() => toggle('unassigned')}
          bodyClassName="max-h-[36vh] overflow-y-auto"
        >
          <UnassignedList
            items={plan?.unassigned || []}
            plan={plan}
            availableDrivers={availableDrivers}
            onAssignUnassigned={onAssignUnassigned}
          />
        </PanelSection>
      </div>
    </aside>
  );
}

function RulesForm({
  selectedDate,
  setSelectedDate,
  strategy,
  setStrategy,
  serviceMinutes,
  setServiceMinutes,
  maxPackagesDefault,
  setMaxPackagesDefault,
  useGoogle,
  setUseGoogle,
  onOpenDrivers,
  onLoadLatestPlan,
  latestPlan,
}) {
  return (
    <div className="space-y-3">
      <label className="block text-xs font-semibold text-slate-600">
        Fecha
        <input
          type="date"
          value={selectedDate}
          onChange={(event) => setSelectedDate(event.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        />
      </label>
      <label className="block text-xs font-semibold text-slate-600">
        Estrategia
        <select
          value={strategy}
          onChange={(event) => setStrategy(event.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        >
          <option value="use_all_available">Usar todos los disponibles</option>
          <option value="use_fewer_drivers">Usar menos conductores</option>
        </select>
      </label>
      <div className="grid grid-cols-2 gap-2">
        <NumberField label="Servicio min" value={serviceMinutes} onChange={setServiceMinutes} />
        <NumberField label="Max default" value={maxPackagesDefault} onChange={setMaxPackagesDefault} />
      </div>
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
        <input type="checkbox" checked={useGoogle} onChange={(event) => setUseGoogle(event.target.checked)} />
        Google Route Optimization
      </label>
      <button
        onClick={onOpenDrivers}
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-bold text-white"
      >
        <Users className="h-4 w-4" />
        Conductores disponibles
      </button>
      {latestPlan && (
        <button
          onClick={onLoadLatestPlan}
          className="w-full rounded-lg border border-brand-primary px-3 py-2 text-sm font-bold text-brand-primary"
        >
          Abrir ultimo plan #{latestPlan.id}
        </button>
      )}
    </div>
  );
}

function buildDestinationGroups(plan, availableDrivers) {
  const routes = plan?.routes || [];
  const routeDriverIds = new Set(routes.map((route) => Number(route.driver_id)).filter(Boolean));
  const routeOptions = routes.map((route) => ({
    value: `route:${route.id}`,
    label: route.driver_name || `Driver ${route.driver_id}`,
  }));
  const driverOptions = (availableDrivers || [])
    .filter((driver) => driver.user_id && !routeDriverIds.has(Number(driver.user_id)))
    .map((driver) => ({
      value: `driver:${driver.user_id}`,
      label: `${driver.name || `Driver ${driver.user_id}`} (nuevo)`,
    }));

  return [
    { label: 'Rutas del plan', options: routeOptions },
    { label: 'Conductores disponibles', options: driverOptions },
  ].filter((group) => group.options.length);
}

function DestinationSelect({ value, onChange, groups }) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2 py-1 text-xs"
    >
      {groups.map((group) => (
        <optgroup key={group.label} label={group.label}>
          {group.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

function UnassignedList({ items = [], plan, availableDrivers = [], onAssignUnassigned }) {
  const [drafts, setDrafts] = useState({});
  const destinationGroups = buildDestinationGroups(plan, availableDrivers);
  const defaultDestination = destinationGroups[0]?.options?.[0]?.value || '';

  if (!items.length) {
    return <div className="rounded-lg bg-emerald-50 p-3 text-sm font-semibold text-emerald-700">No hay entregas sin asignar.</div>;
  }

  async function assign(item) {
    const draft = drafts[item.delivery_point_id] || {};
    const destination = draft.destination || defaultDestination;
    if (!destination) return;
    await onAssignUnassigned(item.delivery_point_id, destination, draft.sequence);
    setDrafts((current) => {
      const next = { ...current };
      delete next[item.delivery_point_id];
      return next;
    });
  }

  return (
    <div className="max-h-[260px] space-y-2 overflow-y-auto">
      {items.map((item) => {
        const draft = drafts[item.delivery_point_id] || {};
        return (
          <div key={`${item.delivery_point_id}-${item.reason}`} className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-sm">
            <div className="flex items-center gap-2 font-bold text-amber-800">
              <AlertTriangle className="h-4 w-4" />
              #{item.delivery_point_id}
            </div>
            <div className="text-xs text-amber-700">{item.delivery?.district || 'Sin distrito'} | {item.reason}</div>
            <div className="mt-2 flex flex-wrap items-center gap-1">
              <DestinationSelect
                value={draft.destination || defaultDestination}
                onChange={(destination) => setDrafts((current) => ({
                  ...current,
                  [item.delivery_point_id]: { ...current[item.delivery_point_id], destination },
                }))}
                groups={destinationGroups}
              />
              <input
                type="number"
                min="1"
                value={draft.sequence || ''}
                onChange={(event) => setDrafts((current) => ({
                  ...current,
                  [item.delivery_point_id]: { ...current[item.delivery_point_id], sequence: event.target.value },
                }))}
                className="w-14 rounded-lg border border-slate-200 px-2 py-1 text-xs"
                placeholder="#"
              />
              <button onClick={() => assign(item)} className="rounded-lg bg-slate-900 px-2 py-1 text-xs font-bold text-white">
                Asignar
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PanelSection({ title, meta, icon, open, onToggle, tone = 'default', bodyClassName = '', children }) {
  return (
    <section className="mb-3 rounded-lg border border-slate-200">
      <button onClick={onToggle} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left">
        <span className="flex min-w-0 items-center gap-2 font-bold text-slate-900">
          {icon}
          <span className="truncate">{title}</span>
        </span>
        <span className={`shrink-0 text-xs font-semibold ${tone === 'warn' ? 'text-amber-700' : 'text-slate-500'}`}>{meta}</span>
      </button>
      {open && <div className={`border-t border-slate-200 p-3 ${bodyClassName}`}>{children}</div>}
    </section>
  );
}

function NumberField({ label, value, onChange }) {
  return (
    <label className="block text-xs font-semibold text-slate-600">
      {label}
      <input
        type="number"
        min="1"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
      />
    </label>
  );
}

function Metric({ label, value, tone }) {
  const classes = {
    warn: 'bg-amber-50 text-amber-700',
    hot: 'bg-pink-50 text-brand-primary',
    ok: 'bg-emerald-50 text-brand-green',
  };
  return (
    <div className={`rounded-lg px-2 py-2 ${classes[tone] || 'bg-slate-100 text-slate-600'}`}>
      <div className="text-lg font-bold">{value}</div>
      <div>{label}</div>
    </div>
  );
}

export default RoutePlannerControlPanel;
